import React from 'react';
import { Formik, Form } from 'formik';
import * as yup from 'yup';
import _ from 'lodash';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Grid,
  Button,
  Checkbox,
  FormControlLabel,
  Box,
  Divider,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useUser } from '../context/UserContext';
import { useSettings } from '../context/SettingsContext';
import { useNavigate } from 'react-router-dom';
import { UnavailabilityCalendar } from './UnavailabilityCalendar';
import { DutyDetailsSection } from './DutyDetailsSection'; 
import { HouseholdMembersSection } from './HouseholdMembersSection';
import  DebugPanel from '../components/DebugPanel';
import Link from '@mui/material/Link';

const RulesLink = ({ children }) => (
  <Link
    component="span"
    onClick={(e) => {
      e.preventDefault();
      window.open('/rules', '_blank', 'noopener,noreferrer');
    }}
    sx={{ 
      color: '#0d2c54',
      textDecoration: 'underline',
      cursor: 'pointer',
      fontWeight: 500,
      '&:hover': {
        color: '#121212',
      }
    }}
  >
    {children}
  </Link>
);


const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(4),
  borderRadius: '16px',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  marginTop: theme.spacing(4),
  marginBottom: theme.spacing(3),
  color: '#0d2c54',
  fontFamily: "'Chillax', sans-serif",
  fontWeight: 500,
}));

const FormSection = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(4),
}));

const MembershipForm = ({ isUpdateOnly = false, memberId = null, isAdminAdd = false }) => {
  const { userData, updateMultipleFields } = useUser();
  const { getSeasonConfig, isFirstSafetyOfficerDeadlinePassed, isSecondSafetyOfficerDeadlinePassed, isLastTheorySessionPassed, getDutyFreezeStatus, maintenance } = useSettings();
  // Block new sign-ups during maintenance, but leave admin manual-add and the
  // logged-in update-details flow working.
  const signupBlocked = maintenance && !isUpdateOnly && !isAdminAdd;
  const freezeStatus = isUpdateOnly ? getDutyFreezeStatus() : { frozen: false, frozenFrom: null, frozenUntil: null, fullFreeze: false };
  const SEASON_CONFIG = getSeasonConfig();
  const navigate = useNavigate();
  const [updateStatus, setUpdateStatus] = React.useState({ loading: false, error: null, success: false });
  const [duplicateDialog, setDuplicateDialog] = React.useState({ open: false, duplicates: [] });
  const [duplicateChecked, setDuplicateChecked] = React.useState({ email: null, phone: null });

  const checkForDuplicates = React.useCallback(async (email, phoneNumber) => {
    if (!isAdminAdd) return;
    if (!email && !phoneNumber) return;
    if (email === duplicateChecked.email && phoneNumber === duplicateChecked.phone) return;

    setDuplicateChecked({ email: email || null, phone: phoneNumber || null });

    try {
      const adminToken = sessionStorage.getItem('adminToken');
      const res = await fetch('/api/admin/check-duplicate-member', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${adminToken}` },
        body: JSON.stringify({ email: email || null, phoneNumber: phoneNumber || null })
      });
      if (res.ok) {
        const data = await res.json();
        if (data.duplicates && data.duplicates.length > 0) {
          setDuplicateDialog({ open: true, duplicates: data.duplicates });
        } else {
          setDuplicateDialog({ open: false, duplicates: [] });
        }
      }
    } catch (err) {
      console.error('Duplicate check failed:', err);
    }
  }, [isAdminAdd, duplicateChecked]);
  
  // Create debounced update function
  const debouncedUpdate = React.useCallback(
    _.debounce((newValues) => {
      updateMultipleFields(newValues);
    }, 500),
    [] // Empty dependency array since we don't want to recreate the debounced function
  );

  // Cleanup on unmount
  React.useEffect(() => {
    return () => {
      debouncedUpdate.cancel();
    };
  }, [debouncedUpdate]);

  const validationSchema = yup.object().shape({
    unavailableDates: yup.array().of(yup.string()),
    contactEmail: isAdminAdd
      ? yup.string("Enter your email").email("Enter a valid email").nullable()
      : yup.string("Enter your email").email("Enter a valid email").required("Email is required"),
    firstName: yup
      .string("Enter your first name")
      .min(2, "Your first name should have a minimum of 2 characters")
      .required("First name is required"),
    lastName: yup
      .string("Enter your last name")
      .min(2, "Your last name should have a minimum of 2 characters")
      .required("Last name is required"),
    phoneNumber: yup
      .string("Please enter a mobile number")
      .required("Mobile number is required")
      .matches(
        /^((\\+[1-9]{1,4}[ \\-]*)|(\\([0-9]{2,3}\\)[ \\-]*)|([0-9]{2,4})[ \\-]*)*?[0-9]{3,4}?[ \\-]*[0-9]{3,4}?$/,
        "Mobile number is not valid"
      ),
    dutyType: yup
      .string()
      .required("Duty type is required")
      .test("valid-duty-type", "Invalid duty type selected", function(value) {
        // Deadlines only constrain NEW registrations; an existing member who
        // is already a 1st/2nd Safety Officer can still update their details
        // after the deadline has passed.
        if (isUpdateOnly) return true;
        if (isFirstSafetyOfficerDeadlinePassed() && value === "1st Safety Officer") {
          return false;
        }
        if (isSecondSafetyOfficerDeadlinePassed() && value === "2nd Safety Officer") {
          return false;
        }
        return true;
      }),
    dutyMon: yup.boolean(),
    dutyTue: yup.boolean(),
    dutyWed: yup.boolean(),
    dutyFri: yup.boolean(),
    dutySat: yup.boolean(),
    dutySun: yup.boolean(),
    dutyHol1: yup.boolean(),
    dutyHol2: yup.boolean(),
    theorySession: yup.string().nullable().when('dutyType', {
      is: '2nd Safety Officer',
      then: (schema) => schema.test(
        'theory-required',
        'Please select a theory session or confirm completion',
        function(value) {
          if (isUpdateOnly) return true;
          if (isLastTheorySessionPassed()) return true;
          const { theoryCompleted } = this.parent;
          if (theoryCompleted) return true;
          return !!value;
        }
      ),
    }),
    poolSession: yup.string().nullable().when('dutyType', {
      is: '2nd Safety Officer',
      then: (schema) => schema.test(
        'pool-required',
        'Please select a pool session',
        function(value) {
          if (isUpdateOnly) return true;
          const { theoryCompleted } = this.parent;
          if (isLastTheorySessionPassed() && theoryCompleted !== true) return true;
          return !!value;
        }
      ),
    }),
    theoryCompleted: yup.mixed().nullable().when('dutyType', {
      is: '2nd Safety Officer',
      then: (schema) => schema.test(
        'theory-completed-check',
        'You must have completed the theory course to register as a 2nd Safety Officer',
        function(value) {
          if (isUpdateOnly) return true;
          if (!isLastTheorySessionPassed()) return true;
          if (value === false) return false;
          if (value === null && isLastTheorySessionPassed()) return false;
          return true;
        }
      ),
    }),
    tandcAcceptance: isUpdateOnly 
      ? yup.boolean()
      : yup.boolean().oneOf([true], "Members must accept the terms and conditions"),
  }).test(
    'at-least-one-duty-day',
    'Please select at least one day for duties',
    function(values) {
      if (isUpdateOnly) return true;
      const hasDutyDay = values.dutyMon || values.dutyTue || values.dutyWed || values.dutyFri || values.dutySat || values.dutySun;
      if (!hasDutyDay) {
        return this.createError({
          path: 'dutyWeekdays',
          message: 'Please select at least one day (weekday or weekend) for duties'
        });
      }
      return true;
    }
  );

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      if (isUpdateOnly) {
        setUpdateStatus({ loading: true, error: null, success: false });
        const memberToken = sessionStorage.getItem('memberToken');
        const response = await fetch('/api/member/update-details', {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${memberToken}`
          },
          body: JSON.stringify({ ...values, memberId })
        });
        const result = await response.json();
        if (response.ok && result.success) {
          setUpdateStatus({ loading: false, error: null, success: true });
        } else {
          setUpdateStatus({ loading: false, error: result.error || 'Failed to update details', success: false });
        }
      } else {
        navigate('/donation', { state: { isAdminAdd } });
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      if (isUpdateOnly) {
        setUpdateStatus({ loading: false, error: 'An error occurred while updating details', success: false });
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Container maxWidth="md">
      <StyledPaper elevation={3}>
        <Typography 
          variant="h4" 
          align="center" 
          gutterBottom
          sx={{
            fontFamily: "'Chillax', sans-serif",
            fontWeight: 500,
            color: '#0d2c54',
            marginBottom: 3,
          }}
        >
          {isUpdateOnly ? 'Update Your Details' : `${SEASON_CONFIG.REGISTRATION_YEAR} Membership Registration`}
        </Typography>
        
        {updateStatus.success && (
          <Alert severity="success" sx={{ mb: 3 }}>
            Details updated.
          </Alert>
        )}
        {updateStatus.error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {updateStatus.error}
          </Alert>
        )}
        
        <Formik
          initialValues={{
            ...userData,
            familyMembers: userData.familyMembers || [],
          }}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          enableReinitialize
          validateOnMount
        >
          {({ values, errors, touched, handleChange, handleBlur, isValid, setFieldValue, submitCount }) => {
            // Update user context whenever form values change
            React.useEffect(() => {
              debouncedUpdate(values);
            }, [values, debouncedUpdate]);



    return (
<Form 
  onKeyDown={(e) => {
    if (e.key === 'Enter' && e.target.tagName !== 'TEXTAREA') {
      e.preventDefault(); // Prevent form submission on Enter
    }
  }}
  // Let Formik handle the submission
  noValidate
>
              {/* Contact Details Section */}
              <FormSection id="section-contact">
                <SectionTitle variant="h5">Contact Details</SectionTitle>
                <Typography variant="body2" gutterBottom>
                  Please enter the name and details of the person who will help out as an admin or safety officer.
                </Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      required
                      name="firstName"
                      label="First Name"
                      variant="outlined"
                      value={values.firstName}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.firstName && Boolean(errors.firstName)}
                      helperText={touched.firstName && errors.firstName}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      required
                      name="lastName"
                      label="Last Name"
                      variant="outlined"
                      value={values.lastName}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.lastName && Boolean(errors.lastName)}
                      helperText={touched.lastName && errors.lastName}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      required
                      name="contactEmail"
                      label="Email"
                      variant="outlined"
                      value={values.contactEmail}
                      onChange={handleChange}
                      onBlur={(e) => {
                        handleBlur(e);
                        if (isAdminAdd && values.contactEmail) {
                          checkForDuplicates(values.contactEmail, values.phoneNumber);
                        }
                      }}
                      error={touched.contactEmail && Boolean(errors.contactEmail)}
                      helperText={touched.contactEmail && errors.contactEmail}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      required
                      name="address"
                      label="Address incl. postcode"
                      variant="outlined"
                      multiline
                      value={values.address}
                      onChange={handleChange}
                      error={touched.address && Boolean(errors.address)}
                      helperText={touched.address && errors.address}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      required
                      name="phoneNumber"
                      label="Mobile Number"
                      variant="outlined"
                      value={values.phoneNumber}
                      onChange={handleChange}
                      onBlur={(e) => {
                        handleBlur(e);
                        if (isAdminAdd && values.phoneNumber) {
                          checkForDuplicates(values.contactEmail, values.phoneNumber);
                        }
                      }}
                      error={touched.phoneNumber && Boolean(errors.phoneNumber)}
                      helperText={touched.phoneNumber && errors.phoneNumber}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={values.rotaPublish}
                          onChange={handleChange}
                          name="rotaPublish"
                        />
                      }
                      label="Please include my email address on the rota so others can contact me for duty swaps."
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={values.inclSmsReminders}
                          onChange={handleChange}
                          name="inclSmsReminders"
                        />
                      }
                      label="I would like to receive SMS text message reminders for upcoming pool duties."
                    />
                  </Grid>
                </Grid>
              </FormSection>

              <Divider />

                {/* Household Members Section */}
                <FormSection>
                  <HouseholdMembersSection 
                    values={values} 
                    handleChange={handleChange}
                    setFieldValue={setFieldValue}
                    isUpdateOnly={isUpdateOnly}
                  />
                </FormSection>

              <Divider />

         {/* Duty Details Section */}
                <Box id="section-duties">
                  <DutyDetailsSection 
                    values={values} 
                    handleChange={handleChange}
                    setFieldValue={setFieldValue}
                    errors={errors} 
                    touched={touched} 
                    submitCount={submitCount}
                    previousDutyType={userData.dutyType}
                    isReturningMember={!!userData.contactEmail}
                    isUpdateOnly={isUpdateOnly}
                    freezeStatus={freezeStatus}
                  />
                </Box>


              {/* Holiday Periods Section */}
              <FormSection>
                <SectionTitle variant="h5">I cannot do duties on:</SectionTitle>
                <Typography variant="body2" gutterBottom>
                  Please select the dates on which you are not able to help out as a{values.dutyType === "Administrator" && "n"} {values.dutyType}
                </Typography>
                <UnavailabilityCalendar 
                  selectedDates={values.unavailableDates || []}
                  onChange={(dates) => {
                    handleChange({
                      target: {
                        name: 'unavailableDates',
                        value: dates
                      }
                    });
                  }}
                  seasonStart={SEASON_CONFIG.SEASON_START}
                  seasonEnd={SEASON_CONFIG.SEASON_END}
                  frozenFrom={freezeStatus.frozen ? freezeStatus.frozenFrom : null}
                  frozenUntil={freezeStatus.frozen ? freezeStatus.frozenUntil : null}
                />
              </FormSection>

              <Divider />

              {/* Terms and Conditions Section - only show for new registrations/renewals */}
              {!isUpdateOnly && (
              <FormSection id="section-declaration">
                <SectionTitle variant="h5">Declaration</SectionTitle>
                <Typography variant="body2" paragraph>
  The Swimming Pool Committee does not accept any responsibility for loss or damage to any property 
  left in the pool area or losses consequent upon such loss or damage, or for injury or death of 
  any person except directly caused through the negligence of the swimming Pool Committee{" "}
  <RulesLink
    component="button"
    onClick={() => window.open('/rules', '_blank', 'noopener,noreferrer')}
    sx={{ 
      color: 'primary.main',
      textDecoration: 'underline',
      '&:hover': {
        color: 'primary.dark',
      }
    }}
  >
    (Rule 19 Members Rulebook)
  </RulesLink>
</Typography>

                <Typography variant="body2" paragraph>
                  DECLARATION: I agree with the terms and Conditions set out above and will abide by the Rules of the 
                  Club as set out in the{" "}
                  <RulesLink
    component="button"
    onClick={() => window.open('/rules', '_blank', 'noopener,noreferrer')}
    sx={{ 
      color: 'primary.main',
      textDecoration: 'underline',
      '&:hover': {
        color: 'primary.dark',
      }
    }}
  >
    Members Rulebook 
  </RulesLink>{" "}
                  (link opens the Rulebook in a new tab/window).
                </Typography>

                <Typography variant="body2" paragraph>
                  In becoming a CCSC member I hereby agree to be bound by the terms of the Data Protection Act 1998 
                  (as amended by the General Data Protection Regulations 2018) in respect of not sharing or disclosing 
                  any personal data of CCSC Members or associates outside of CCSC without their prior consent. I further 
                  agree to CCSC using my personal data (including my name and relevant contact details) to share with 
                  other CCSC members for administrative purposes; (e.g. Admin / Safety Officer Duty rosters and amendments). 
                  CCSC for its part will ensure the confidentiality and privacy of all personal data it holds on behalf 
                  of its Members.
                </Typography>

                <FormControlLabel
                  control={
                    <Checkbox
                      required
                      checked={values.tandcAcceptance}
                      onChange={handleChange}
                      name="tandcAcceptance"
                    />
                  }
                  label="I accept the terms and conditions"
                />
                {errors.tandcAcceptance && (
                  <Box sx={{ 
                    mt: 1, 
                    p: 1.5, 
                    backgroundColor: '#F2F4CC', 
                    borderRadius: '8px',
                    borderLeft: '4px solid #0d2c54'
                  }}>
                    <Typography variant="body2" sx={{ color: '#0d2c54' }}>
                      Please accept the terms and conditions to continue
                    </Typography>
                  </Box>
                )}
              </FormSection>
              )}

              {/* Form Guidance Messages */}
              {Object.keys(errors).length > 0 && (
                <Box sx={{ 
                  mt: 3, 
                  mb: 2, 
                  p: 2.5, 
                  backgroundColor: '#F2F4CC', 
                  borderRadius: '12px',
                  borderLeft: '4px solid #0d2c54'
                }}>
                  <Typography variant="h6" sx={{ color: '#0d2c54', fontFamily: "'Chillax', sans-serif", mb: 1.5 }}>
                    To submit this form, please:
                  </Typography>
                  {Object.entries(errors).map(([key, value]) => {
                    const scrollToSection = (sectionId) => {
                      const element = document.getElementById(sectionId);
                      if (element) {
                        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      }
                    };
                    
                    const errorConfig = {
                      firstName: { action: 'Enter your first name', section: 'section-contact' },
                      lastName: { action: 'Enter your last name', section: 'section-contact' },
                      contactEmail: { action: 'Enter a valid email address', section: 'section-contact' },
                      address: { action: 'Enter your address', section: 'section-contact' },
                      phoneNumber: { action: 'Enter a valid mobile number', section: 'section-contact' },
                      dutyWeekdays: { action: 'Select at least one day for duties', section: 'section-duties' },
                      tandcAcceptance: { action: 'Accept the terms and conditions', section: 'section-declaration' },
                    };
                    
                    const config = errorConfig[key] || { action: value, section: null };
                    
                    return (
                      <Typography 
                        key={key} 
                        sx={{ 
                          color: '#0d2c54', 
                          mb: 0.5,
                          cursor: config.section ? 'pointer' : 'default',
                          '&:hover': config.section ? { textDecoration: 'underline' } : {}
                        }}
                        onClick={() => config.section && scrollToSection(config.section)}
                      >
                        • {config.action} {config.section && <span style={{ fontSize: '0.85em', opacity: 0.7 }}>↑</span>}
                      </Typography>
                    );
                  })}
                </Box>
              )}

              {/* Submit Button */}
              <Box sx={{ mt: 4, mb: 2, textAlign: 'center' }}>
                {signupBlocked && (
                  <Typography sx={{ mb: 2, color: '#7a3e00', fontWeight: 500 }}>
                    Membership sign-up is temporarily unavailable while the site is undergoing maintenance. Please check back shortly.
                  </Typography>
                )}
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  disabled={signupBlocked || !isValid || Object.keys(errors).length > 0}
                  sx={{ 
                    minWidth: 200,
                    backgroundColor: '#dbf14e',
                    color: '#121212',
                    fontFamily: "'Chillax', sans-serif",
                    fontWeight: 500,
                    fontSize: '18px',
                    padding: '12px 32px',
                    borderRadius: '100px',
                    border: '2px solid #121212',
                    textTransform: 'none',
                    '&:hover': {
                      backgroundColor: '#c8dc45',
                    },
                    '&:disabled': {
                      backgroundColor: '#e8e8e8',
                      color: '#999999',
                      borderColor: '#cccccc',
                    }
                  }}
                >
                  {updateStatus.loading ? 'Saving...' : (isUpdateOnly ? 'Save Changes' : 'Submit Registration')}
                </Button>
              </Box>
                {/* Debug Panel */}
                {process.env.NODE_ENV === 'development' && (
                  <DebugPanel formValues={values} contextData={userData} />
                )}
              </Form>
    );
  }}
</Formik>
      </StyledPaper>

      {isAdminAdd && (
        <Dialog
          open={duplicateDialog.open}
          onClose={() => setDuplicateDialog({ open: false, duplicates: [] })}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{ fontFamily: "'Chillax', sans-serif", color: '#0d2c54', fontWeight: 600 }}>
            Existing Member Found
          </DialogTitle>
          <DialogContent>
            <Typography variant="body2" sx={{ mb: 2 }}>
              {duplicateDialog.duplicates.length === 1
                ? 'A member with matching details already exists. Would you like to edit their record instead?'
                : 'Members with matching details already exist. Would you like to edit one of their records instead?'}
            </Typography>
            {duplicateDialog.duplicates.map((dup) => (
              <Box
                key={dup.id}
                sx={{
                  p: 2,
                  mb: 1,
                  border: '1px solid #e0e0e0',
                  borderRadius: '8px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  '&:hover': { backgroundColor: '#f5f5f5' }
                }}
              >
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    {dup.firstName} {dup.lastName}
                  </Typography>
                  {dup.email && (
                    <Typography variant="body2" color="text.secondary">
                      {dup.email}
                    </Typography>
                  )}
                  {dup.phone && (
                    <Typography variant="body2" color="text.secondary">
                      {dup.phone}
                    </Typography>
                  )}
                  <Box sx={{ mt: 0.5, display: 'flex', gap: 0.5 }}>
                    <Chip
                      label={`Matched on ${dup.matchedOn === 'both' ? 'email & phone' : dup.matchedOn}`}
                      size="small"
                      sx={{ fontSize: '0.7rem', height: 20 }}
                    />
                    {dup.isCurrentYear && (
                      <Chip
                        label="Registered this year"
                        size="small"
                        color="success"
                        sx={{ fontSize: '0.7rem', height: 20 }}
                      />
                    )}
                  </Box>
                </Box>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => {
                    setDuplicateDialog({ open: false, duplicates: [] });
                    navigate(`/admin?tab=members&editMember=${dup.id}`);
                  }}
                  sx={{
                    borderRadius: '100px',
                    textTransform: 'none',
                    borderColor: '#0d2c54',
                    color: '#0d2c54',
                    '&:hover': { backgroundColor: '#0d2c54', color: '#fff' }
                  }}
                >
                  Edit
                </Button>
              </Box>
            ))}
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button
              onClick={() => setDuplicateDialog({ open: false, duplicates: [] })}
              sx={{ textTransform: 'none', color: '#666' }}
            >
              Continue Adding New Member
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Container>
  );
};

export default MembershipForm;